import React, { useEffect, useState, useRef } from "react";
import ApiService from "../../../Services/user/service";
import socket from "../../../Utils/socketClient";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

export default function OptionChain() {
  const location = useLocation();
  const [expiryList, setExpiryList] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [optionChain, setOptionChain] = useState([]);
  const [livePrice, setLivePrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [symbolSearch, setSymbolSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expiryListInr, setExpiryListInr] = useState([]);
  const userid = localStorage.getItem("user_id");
  const currency = localStorage.getItem("currentCurrency");
  // Order placement states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    symbol: "",
    price: 0,
    type: "buy",
    optionType: "", // CE or PE
    strike: 0,
    selectedOption: "Market", // Market, Limit, Stop
    lot: 1,
    qty: 1,
    lotsize: 1,
  });
  const [orderLoading, setOrderLoading] = useState(false);
  const [listDataInr, setListDataInr] = useState([]);
  const currentExpiryRef = useRef("");
  const APP_VERSION = "1.0.0"; // Update this with your app version
  const dropdownRef = useRef(null);

  // SCROLL TO ATM LOGIC
  const atmRowRef = useRef(null);
  const [lastScrolledUSD, setLastScrolledUSD] = useState("");
  const [lastScrolledINR, setLastScrolledINR] = useState("");

  useEffect(() => {
    if (currency === "0" && optionChain.length > 0 && selectedExpiry !== lastScrolledUSD && atmRowRef.current) {
      setTimeout(() => {
        atmRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
      setLastScrolledUSD(selectedExpiry);
    }
  }, [optionChain, currency, selectedExpiry, lastScrolledUSD]);

  useEffect(() => {
    if (currency === "1" && listDataInr.length > 0 && (selectedExpiry + symbolSearch) !== lastScrolledINR && atmRowRef.current) {
      setTimeout(() => {
        atmRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
      setLastScrolledINR(selectedExpiry + symbolSearch);
    }
  }, [listDataInr, currency, selectedExpiry, symbolSearch, lastScrolledINR]);




  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSearchResults([]);   // Dropdown close
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // 1️⃣ SOCKET CONNECTION STATUS
  useEffect(() => {
    const handleConnect = () => {
      setSocketConnected(true);

      const userPlan = localStorage.getItem("user_plan") || "Basic";
      socket.emit("join_plan", userPlan);

      if (currentExpiryRef.current) {
        socket.emit("subscribe_expiry", currentExpiryRef.current);
      }
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  // 2️⃣ FETCH EXPIRY LIST
  useEffect(() => {
    const fetchExpiry = async () => {
      try {
        const res = await ApiService.getExpiry();
        if (res.data.status) {
          setExpiryList(res.data.data);
        }
      } catch (err) {
        console.error("❌ Expiry fetch error:", err);
      }
    };

    fetchExpiry();
  }, []);

  // 🔥 AUTO SELECT FIRST EXPIRY FOR USD
  useEffect(() => {
    if (currency === "0" && expiryList.length > 0) {
      const firstExpiry = expiryList[0];

      // agar pehle se same expiry set hai to dobara mat chalao
      if (currentExpiryRef.current === firstExpiry) return;


      setSelectedExpiry(firstExpiry);
      currentExpiryRef.current = firstExpiry;

      // 🔥 API CALL
      fetchStrikeData(firstExpiry);

      // 🔥 SOCKET SUBSCRIBE
      if (socket.connected) {
        socket.emit("subscribe_expiry", firstExpiry);
      }
    }
  }, [expiryList, currency, socketConnected]);


  // 3️⃣ FETCH STRIKE LIST
  const fetchStrikeData = async (expiry) => {
    setLoading(true);

    try {
      const res = await ApiService.createStrike({
        expiry,
        userid: localStorage.getItem("user_id"),
      });

      if (res.data.status) {
        const mapped = res.data.data.map((item) => ({
          ...item,
          strike: Number(item.strike) || 0,
          CE: Number(item.CE) || 0,
          PE: Number(item.PE) || 0,
          symbolCE: item.symbolCE || "",
          symbolPE: item.symbolPE || "",
        }));

        setOptionChain(mapped);
        setLivePrice(Number(res.data.liveprice) || 0);
      }
    } catch (err) {
      console.error("❌ Strike API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // 4️⃣ HANDLE EXPIRY SELECT
  const handleExpirySelect = (e) => {
    const expiry = e.target.value;
    setSelectedExpiry(expiry);
    currentExpiryRef.current = expiry;

    if (!expiry) {
      setOptionChain([]);
      return;
    }

    fetchStrikeData(expiry);

    if (socket.connected) {
      socket.emit("subscribe_expiry", expiry);
    }
  };

  // 5️⃣ SOCKET → LIVE UPDATES
  useEffect(() => {
    const dataHandler = (payload) => {
      if (!payload) return;

      const { data } = payload;

      // UPDATE BTC LIVE PRICE
      if (data?.ticker?.toLowerCase() === "btcusd") {
        const newPrice = Number(data.Mid_Price);
        if (!isNaN(newPrice) && newPrice > 0) {
          setLivePrice(newPrice);
          setLastUpdate(new Date().toLocaleTimeString());
        }
      }

      // UPDATE STRIKE PRICES (CE/PE)
      if (data?.ticker && currentExpiryRef.current) {
        const ticker = data.ticker.toLowerCase();

        setOptionChain((prev) => {
          let updated = false;

          const newChain = prev.map((item) => {
            if (item.symbolCE && item.symbolCE.toLowerCase() === ticker) {
              const cePrice = Number(data.Mid_Price);
              if (!isNaN(cePrice) && cePrice > 0) {
                updated = true;
                return { ...item, CE: cePrice };
              }
            }

            if (item.symbolPE && item.symbolPE.toLowerCase() === ticker) {
              const pePrice = Number(data.Mid_Price);
              if (!isNaN(pePrice) && pePrice > 0) {
                updated = true;
                return { ...item, PE: pePrice };
              }
            }

            return item;
          });

          if (updated) {
            setLastUpdate(new Date().toLocaleTimeString());
          }

          return updated ? newChain : prev;
        });
      }
    };

    socket.on("receive_data_forex", dataHandler);
    return () => socket.off("receive_data_forex", dataHandler);
  }, []);

  // 6️⃣ OPEN ORDER MODAL
  const openOrderModal = (symbol, price, type, optionType, strike, symbolid, lotsize) => {
    const defaultLotSize = Number(lotsize) || 1;
    setOrderDetails({
      symbol,
      price,
      type,
      optionType,
      strike,
      selectedOption: "Market",
      lot: 1,
      qty: defaultLotSize,
      lotsize: defaultLotSize,
      symbolid: symbolid || "",
    });
    setShowOrderModal(true);
  };

  // 7️⃣ PLACE ORDER
  const handlePlaceOrder = async () => {
    setOrderLoading(true);

    try {
      const userid = localStorage.getItem("user_id");

      if (!userid) {
        alert("User not logged in!");
        setOrderLoading(false);
        return;
      }

      const requiredFund = orderDetails.price * orderDetails.qty;

      const orderPayload = {
        userid,
        symbol: orderDetails.symbol,
        price: orderDetails.selectedOption === "Market"
          ? orderDetails.price
          : Number(orderDetails.limitStopPrice || orderDetails.price),
        lot: orderDetails.lot,
        qty: orderDetails.qty,
        requiredFund: parseFloat(requiredFund.toFixed(2)),
        type: orderDetails.type.toUpperCase(),
        lotsize: orderDetails.lotsize,
        With_Margin: false,
        selectedOption: orderDetails.selectedOption,
        token: orderDetails.symbol,
        symbolid: orderDetails.symbolid,
        margin_type: "normal",
        app_version: currency === "1" ? "1.0.0" : "1.0.19",
      };


      let response;
      if (currency === "1") {
        response = await ApiService.placeOrderInr(orderPayload);
      } else {
        response = await ApiService.placeOrder(orderPayload);
      }

      if (response.data.status) {
        toast.success(`✅ Order Placed Successfully!\nOrder ID: ${response.data.order?._id || "Done"}`);
        setShowOrderModal(false);

        // Reset order details
        setOrderDetails({
          symbol: "",
          price: 0,
          type: "buy",
          optionType: "",
          strike: 0,
          selectedOption: "Market",
          lot: 1,
          qty: 1,
          lotsize: 1,
          symbolid: "",
        });
      } else {
        toast.error(`❌ Order Failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error("❌ Order Error:", error);
      toast.error(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setOrderLoading(false);
    }
  };



  // 8️⃣ CALCULATE ATM STRIKE
  const atmStrike =
    livePrice && optionChain.length > 0
      ? optionChain.reduce((prev, curr) =>
        Math.abs(curr.strike - livePrice) < Math.abs(prev.strike - livePrice)
          ? curr
          : prev
      )
      : null;


  const handleSymbolSearch = async (value) => {

    try {
      setSearchLoading(true);

      const res = await ApiService.optionSymbolSearch({
        symbol: value,
      });

      if (res.data.status) {
        setSearchResults(res.data.data);
        handleExpirySelectInr(value);
        // handleshowalldatainr(value);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("INR Symbol Search Error:", err);
    } finally {
      setSearchLoading(false);
    }
  };


  const handleExpirySelectInr = async (value) => {
    try {
      const res = await ApiService.optionSymbolExpiry({
        symbol: value,
      });

      if (res.data.status) {
        const list = res.data.data;
        setExpiryListInr(list);

        if (list.length > 0) {
          const firstExpiry = list[0].expiry;

          setSelectedExpiry(firstExpiry);
          handleshowalldatainr(value, firstExpiry);
        }
      } else {
        setExpiryListInr([]);
        setSelectedExpiry("");
      }
    } catch (err) {
      console.error("INR Symbol Expiry Error:", err);
    }
  }

  const subscribedChannelsRef = useRef(null);

  const handleshowalldatainr = async (value, expiry) => {
    try {
      const data = { userid, symbol: value, expiry };
      const res = await ApiService.optionRoundToken(data);

      if (res.data.status) {
        setListDataInr(res.data.data);

        // UNSUBSCRIBE PREVIOUS CHANNELS
        if (subscribedChannelsRef.current) {
          socket.emit("unsubscribe", subscribedChannelsRef.current);
        }

        // 🔥 ALICEBLUE SUBSCRIBE
        socket.emit("subscribe", res.data.channellist);
        subscribedChannelsRef.current = res.data.channellist;
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    return () => {
      if (subscribedChannelsRef.current) {
        socket.emit("unsubscribe", subscribedChannelsRef.current);
      }
    };
  }, []);


  useEffect(() => {
    const handleAliceTick = (tick) => {
      // console.log("🔥 ALICE TICK:", tick);

      if (!tick?.tk || !tick?.lp) return;

      const token = String(tick.tk);
      const price = Number(tick.lp);
      if (!price || price <= 0) return;

      setListDataInr((prev) =>
        prev.map((item) => {
          if (String(item.call_token) === token) {
            return { ...item, call_price: price };
          }
          if (String(item.put_token) === token) {
            return { ...item, put_price: price };
          }
          return item;
        })
      );

      setLastUpdate(new Date().toLocaleTimeString());
    };

    socket.on("alice_ticks", handleAliceTick);

    return () => {
      socket.off("alice_ticks", handleAliceTick);
    };
  }, []);




  useEffect(() => {
    socket.on("connect", () => {
      console.log("🟢 AliceBlue socket connected");

    });

  }, []);

  useEffect(() => {
    if (location.state && location.state.symbol) {
      const sym = location.state.symbol;
      setSymbolSearch(sym);
      handleExpirySelectInr(sym);
      // Clear the state so it doesn't loop or stay stuck
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <div className="container-fluid py-2" style={{ maxWidth: "1200px" , marginTop:"20px" }}>
      {/* COMPACT TOP HEADER BAR */}
      <div className="card shadow-sm border-0 mb-3 rounded-3">
        <div className="card-body py-2 px-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">

            {/* Title & Status */}
            <div className="d-flex align-items-center gap-2">
              <span
                className="badge rounded-circle p-1 d-inline-block"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: socketConnected ? "#198754" : "#ffc107"
                }}
                title={socketConnected ? "Socket Connected" : "Socket Disconnected"}
              ></span>
              <h6 className="fw-bold text-primary mb-0" style={{ fontSize: "14px" }}>
                {currency === "1" ? `${symbolSearch || "NIFTY"} Option Chain` : "BTC Option Chain"}
              </h6>
              {currency === "0" && livePrice && (
                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-1 px-2" style={{ fontSize: "11px" }}>
                  Live BTC: <b>${livePrice.toFixed(2)}</b>
                </span>
              )}
              {lastUpdate && (
                <small className="text-muted" style={{ fontSize: "10px" }}>
                  Updated: {lastUpdate}
                </small>
              )}
            </div>

            {/* Controls */}
            <div className="d-flex align-items-center gap-2">
              {currency === "1" && (
                <div className="position-relative" ref={dropdownRef} style={{ width: "150px" }}>
                  <input
                    type="text"
                    style={{ height: "28px", fontSize: "11px" }}
                    className="form-control rounded-2 py-1 px-2"
                    placeholder="Search Symbol..."
                    value={symbolSearch}
                    onChange={(e) => {
                      setSymbolSearch(e.target.value);
                      handleSymbolSearch(e.target.value);
                    }}
                  />
                  {searchLoading && (
                    <div
                      className="position-absolute end-0 top-50 translate-middle-y me-2 spinner-border spinner-border-sm text-muted"
                      style={{ width: "10px", height: "10px", borderWidth: "1.5px" }}
                    ></div>
                  )}
                  {searchResults.length > 0 && (
                    <ul
                      className="list-group position-absolute w-100 shadow border-0"
                      style={{ zIndex: 10, maxHeight: "150px", overflowY: "auto", fontSize: "11px" }}
                    >
                      {searchResults.map((item, index) => (
                        <li
                          key={index}
                          className="list-group-item list-group-item-action py-1 px-2"
                          onClick={() => {
                            const symbol = item.symbol;
                            setSymbolSearch(symbol);
                            setSearchResults([]);
                            handleExpirySelectInr(symbol);
                            handleshowalldatainr(symbol);
                          }}
                        >
                          {item.symbol}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {currency === "1" && (
                <div style={{ width: "150px" }}>
                  <select
                    style={{ height: "28px", fontSize: "11px" }}
                    className="form-select rounded-2 py-1 px-2"
                    value={selectedExpiry}
                    onChange={(e) => {
                      const expiry = e.target.value;
                      setSelectedExpiry(expiry);
                      handleExpirySelectInr(symbolSearch);
                      handleshowalldatainr(symbolSearch, expiry);
                    }}
                  >
                    <option value="">-- Select Expiry --</option>
                    {expiryListInr.map((exp, i) => (
                      <option key={i} value={exp.expiry}>{exp.expiryDate.split("T")[0]}</option>
                    ))}
                  </select>
                </div>
              )}

              {currency === "0" && (
                <div style={{ width: "150px" }}>
                  <select
                    style={{ height: "28px", fontSize: "11px" }}
                    className="form-select rounded-2 py-1 px-2"
                    value={selectedExpiry}
                    onChange={handleExpirySelect}
                    disabled={!socketConnected}
                  >
                    <option value="">-- Select Expiry --</option>
                    {expiryList.map((exp, i) => (
                      <option key={i} value={exp}>{exp}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {!loading && optionChain.length > 0 && (
        <div className="card shadow-lg border-0 rounded-4 mt-4">
          <div className="card-body p-0 table-responsive">
            <div>
              {/* FIXED HEADER TABLE */}
              <table className="table table-hover text-center align-middle mb-0">
                <thead className="bg-primary text-white" style={{ fontSize: "12px" }}>
                  <tr>
                    <th className="py-2" style={{ width: "42%", color: "#fff" }}>CALL (CE)</th>
                    <th className="py-2" style={{ width: "16%", color: "#fff" }}>STRIKE</th>
                    <th className="py-2" style={{ width: "42%", color: "#fff" }}>PUT (PE)</th>
                  </tr>
                </thead>
              </table>

              <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                <table className="table table-hover text-center align-middle mb-0">
                  <tbody>
                    {optionChain.map((item, i) => {
                      const isATM = atmStrike?.strike === item.strike;

                      return (
                        <tr ref={isATM ? atmRowRef : null} key={i} className={isATM ? "bg-warning bg-opacity-25" : ""} style={{ fontSize: "12px" }}>
                          {/* CALL (CE) */}
                          <td className="py-1 px-2" style={{ width: "42%" }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-success py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.symbolCE,
                                      item.CE,
                                      "buy",
                                      "CE",
                                      item.strike,
                                      item._id,
                                      1
                                    )
                                  }
                                  disabled={!socketConnected}
                                >
                                  B
                                </button>
                                <button
                                  className="btn btn-danger py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.symbolCE,
                                      item.CE,
                                      "sell",
                                      "CE",
                                      item.strike,
                                      item._id,
                                      1
                                    )
                                  }
                                  disabled={!socketConnected}
                                >
                                  S
                                </button>
                              </div>
                              <div className="text-end">
                                <span className="fw-bold text-success" style={{ fontSize: "13px" }}>
                                  ${Number(item.CE || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* STRIKE */}
                          <td className="py-1 px-2 fw-bold" style={{ width: "16%", fontSize: "13px" }}>
                            ${item.strike}
                            {isATM && (
                              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: "9px", padding: "2px 4px" }}>
                                ATM
                              </span>
                            )}
                          </td>

                          {/* PUT (PE) */}
                          <td className="py-1 px-2" style={{ width: "42%" }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="text-start">
                                <span className="fw-bold text-danger" style={{ fontSize: "13px" }}>
                                  ${Number(item.PE || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-success py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.symbolPE,
                                      item.PE,
                                      "buy",
                                      "PE",
                                      item.strike,
                                      item._id,
                                      1
                                    )
                                  }
                                  disabled={!socketConnected}
                                >
                                  B
                                </button>
                                <button
                                  className="btn btn-danger py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.symbolPE,
                                      item.PE,
                                      "sell",
                                      "PE",
                                      item.strike,
                                      item._id,
                                      1
                                    )
                                  }
                                  disabled={!socketConnected}
                                >
                                  S
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}


      {currency === "1" && (
        <div className="card shadow-lg border-0 rounded-4 mt-4">
          <div className="card-body p-0 table-responsive">
            <div>
              {/* FIXED HEADER TABLE */}
              <table className="table table-hover text-center align-middle mb-0">
                <thead className="bg-primary text-white" style={{ fontSize: "12px" }}>
                  <tr>
                    <th className="py-2" style={{ width: "42%", color: "#fff" }}>CALL (CE)</th>
                    <th className="py-2" style={{ width: "16%", color: "#fff" }}>STRIKE</th>
                    <th className="py-2" style={{ width: "42%", color: "#fff" }}>PUT (PE)</th>
                  </tr>
                </thead>
              </table>

              {/* SCROLLABLE BODY */}
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <table className="table table-hover text-center align-middle mb-0">
                  <tbody>
                    {listDataInr.map((item, i) => {
                      const atmStrikeInr = listDataInr.length > 0 ? listDataInr[Math.floor(listDataInr.length / 2)] : null;
                      const isATM = atmStrikeInr?.strike_price === item.strike_price;

                      return (
                        <tr ref={isATM ? atmRowRef : null} key={i} className={isATM ? "bg-warning bg-opacity-25" : ""} style={{ fontSize: "12px" }}>
                          {/* CALL (CE) */}
                          <td className="py-1 px-2" style={{ width: "42%" }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-success py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.call_tradesymbol,
                                      item.call_price,
                                      "buy",
                                      "CE",
                                      item.strike_price,
                                      item.call_id,
                                      Number(item.lotsize) || 1
                                    )
                                  }
                                >
                                  B
                                </button>
                                <button
                                  className="btn btn-danger py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.call_tradesymbol,
                                      item.call_price,
                                      "sell",
                                      "CE",
                                      item.strike_price,
                                      item.call_id,
                                      Number(item.lotsize) || 1
                                    )
                                  }
                                >
                                  S
                                </button>
                              </div>
                              <div className="text-end">
                                {/* <div className="text-muted" style={{ fontSize: "9px" }}>{item.call_tradesymbol}</div> */}
                                <span className="fw-bold text-success" style={{ fontSize: "13px" }}>
                                  ₹{Number(item.call_price || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* STRIKE */}
                          <td className="py-1 px-2 fw-bold" style={{ width: "16%", fontSize: "13px" }}>
                            ₹{item.strike_price}
                            {isATM && (
                              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: "9px", padding: "2px 4px" }}>
                                ATM
                              </span>
                            )}
                          </td>

                          {/* PUT (PE) */}
                          <td className="py-1 px-2" style={{ width: "42%" }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="text-start">
                                {/* <div className="text-muted" style={{ fontSize: "9px" }}>{item.put_tradesymbol}</div> */}
                                <span className="fw-bold text-danger" style={{ fontSize: "13px" }}>
                                  ₹{Number(item.put_price || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-success py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.put_tradesymbol,
                                      item.put_price,
                                      "buy",
                                      "PE",
                                      item.strike_price,
                                      item.put_id,
                                      Number(item.lotsize) || 1
                                    )
                                  }
                                >
                                  B
                                </button>
                                <button
                                  className="btn btn-danger py-0 px-2 fw-semibold"
                                  style={{ fontSize: "11px", height: "22px" }}
                                  onClick={() =>
                                    openOrderModal(
                                      item.put_tradesymbol,
                                      item.put_price,
                                      "sell",
                                      "PE",
                                      item.strike_price,
                                      item.put_id,
                                      Number(item.lotsize) || 1
                                    )
                                  }
                                >
                                  S
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ORDER MODAL */}
      {showOrderModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {orderDetails.type.toUpperCase()} {orderDetails.optionType} @ Strike {currency === "1" ? "₹" : "$"}{orderDetails.strike}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowOrderModal(false)}
                  disabled={orderLoading}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Symbol</label>
                  <input
                    type="text"
                    className="form-control"
                    value={orderDetails.symbol}
                    disabled
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Order Type</label>
                  <select
                    className="form-select"
                    value={orderDetails.selectedOption}
                    onChange={(e) => setOrderDetails({ ...orderDetails, selectedOption: e.target.value })}
                  >
                    <option value="Market">Market</option>
                    <option value="Limit">Limit</option>
                    <option value="Stop">Stop</option>
                  </select>
                </div>

                {(orderDetails.selectedOption === "Limit" || orderDetails.selectedOption === "Stop") && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Limit/Stop Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.limitStopPrice || orderDetails.price}
                      onChange={(e) => setOrderDetails({ ...orderDetails, limitStopPrice: e.target.value })}
                      step="0.01"
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={orderDetails.price}
                    disabled
                  />
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Lot</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.lot}
                      onChange={(e) => {
                        const nextLot = parseInt(e.target.value) || 1;
                        setOrderDetails({
                          ...orderDetails,
                          lot: nextLot,
                          qty: nextLot * orderDetails.lotsize,
                        });
                      }}
                      min="1"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.qty}
                      onChange={(e) => setOrderDetails({ ...orderDetails, qty: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Lot Size</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.lotsize}
                      onChange={(e) => setOrderDetails({ ...orderDetails, lotsize: parseInt(e.target.value) || 1 })}
                      min="1"
                      disabled
                    />
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>Required Fund:</strong> {currency === "1" ? "₹" : "$"}
                  {(orderDetails.price * orderDetails.qty).toFixed(2)}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowOrderModal(false)}
                  disabled={orderLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-${orderDetails.type === 'buy' ? 'success' : 'danger'}`}
                  onClick={handlePlaceOrder}
                  disabled={orderLoading}
                >
                  {orderLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    `Confirm ${orderDetails.type.toUpperCase()}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}